/**
 * Scope-dispatched TanStack Query helpers for the daemon (`system`) and employee (`org`) mounts.
 *
 * The server mounts one handler at two paths, so the generator emits a `*Daemon*` and a `*Employee*` operation with
 * identical shapes. Each helper here picks the generated `xOptions` / `xQueryKey` / `xMutation` for the scope and is
 * annotated with the daemon-side type. That collapses the spurious union without a cast: if the two mounts ever
 * diverge, this file stops compiling.
 */
import {
  assignDaemonWorkflowMutation,
  assignEmployeeWorkflowMutation,
  createDaemonMutation,
  createDaemonTriggerMutation,
  createEmployeeMutation,
  createEmployeeTriggerMutation,
  deleteDaemonMutation,
  deleteDaemonTriggerMutation,
  deleteEmployeeMutation,
  deleteEmployeeTriggerMutation,
  disableDaemonMutation,
  disableEmployeeMutation,
  enableDaemonMutation,
  enableEmployeeMutation,
  getDaemonOptions,
  getDaemonQueryKey,
  getDaemonReadinessOptions,
  getDaemonReadinessQueryKey,
  getDaemonRunAuditOptions,
  getDaemonsSummaryOptions,
  getDaemonsSummaryQueryKey,
  getDaemonTrendOptions,
  getEmployeeOptions,
  getEmployeeQueryKey,
  getEmployeeReadinessOptions,
  getEmployeeReadinessQueryKey,
  getEmployeeRunAuditOptions,
  getEmployeeTrendOptions,
  getWorkforceSummaryOptions,
  getWorkforceSummaryQueryKey,
  grantDaemonRunExecutionEnvelopePermissionMutation,
  grantEmployeeRunExecutionEnvelopePermissionMutation,
  listDaemonFindingsOptions,
  listDaemonFindingsQueryKey,
  listDaemonPermissionGrantsOptions,
  listDaemonPermissionGrantsQueryKey,
  listDaemonReflectionsOptions,
  listDaemonRunDecisionsOptions,
  listDaemonRunExecutionEnvelopesOptions,
  listDaemonRunExecutionEnvelopesQueryKey,
  listDaemonRunReportsOptions,
  listDaemonRunsOptions,
  listDaemonRunsQueryKey,
  listDaemonsOptions,
  listDaemonsQueryKey,
  listDaemonTriggersOptions,
  listDaemonTriggersQueryKey,
  listDaemonWorkflowsOptions,
  listDaemonWorkflowsQueryKey,
  listEmployeeFindingsOptions,
  listEmployeeFindingsQueryKey,
  listEmployeePermissionGrantsOptions,
  listEmployeePermissionGrantsQueryKey,
  listEmployeeReflectionsOptions,
  listEmployeeRunDecisionsOptions,
  listEmployeeRunExecutionEnvelopesOptions,
  listEmployeeRunExecutionEnvelopesQueryKey,
  listEmployeeRunReportsOptions,
  listEmployeeRunsOptions,
  listEmployeeRunsQueryKey,
  listEmployeesOptions,
  listEmployeesQueryKey,
  listEmployeeTriggersOptions,
  listEmployeeTriggersQueryKey,
  listEmployeeWorkflowsOptions,
  listEmployeeWorkflowsQueryKey,
  removeDaemonWorkflowMutation,
  removeEmployeeWorkflowMutation,
  resetDaemonGovernanceMutation,
  resetEmployeeGovernanceMutation,
  resumeAllDaemonsMutation,
  resumeAllEmployeesMutation,
  revokeDaemonPermissionGrantMutation,
  revokeEmployeePermissionGrantMutation,
  runDaemonMutation,
  runEmployeeMutation,
  terminateAllDaemonsMutation,
  terminateAllEmployeesMutation,
  updateDaemonMutation,
  updateDaemonTriggerMutation,
  updateEmployeeMutation,
  updateEmployeeTriggerMutation,
} from '../../../generated/api/@tanstack/react-query.gen.js';

import type { ListDaemonsData } from '../../../generated/api/types.gen.js';

export type DaemonScope = 'system' | 'org';

const path = (id: number) => ({ id: String(id) });

/** Reads for one scope. Each returns generated query options, ready for `useQuery`. */
export function daemonReads(scope: DaemonScope) {
  const s = scope === 'system';
  return {
    list: (query?: ListDaemonsData['query']) => (s ? listDaemonsOptions({ query }) : listEmployeesOptions({ query })),
    get: (id: number) => (s ? getDaemonOptions({ path: path(id) }) : getEmployeeOptions({ path: path(id) })),
    summary: () => (s ? getDaemonsSummaryOptions() : getWorkforceSummaryOptions()),
    readiness: (id: number) =>
      s ? getDaemonReadinessOptions({ path: path(id) }) : getEmployeeReadinessOptions({ path: path(id) }),
    trend: (id: number) =>
      s ? getDaemonTrendOptions({ path: path(id) }) : getEmployeeTrendOptions({ path: path(id) }),
    runs: (
      id: number,
      query?: { limit?: string; status?: 'pending' | 'running' | 'completed' | 'failed' | 'skipped' },
    ) => (s ? listDaemonRunsOptions({ path: path(id), query }) : listEmployeeRunsOptions({ path: path(id), query })),
    runAudit: (id: number, runId: number) =>
      s
        ? getDaemonRunAuditOptions({ path: { id: String(id), runId: String(runId) } })
        : getEmployeeRunAuditOptions({ path: { id: String(id), runId: String(runId) } }),
    runReports: (id: number, runId: number) =>
      s
        ? listDaemonRunReportsOptions({ path: { id: String(id), runId: String(runId) } })
        : listEmployeeRunReportsOptions({ path: { id: String(id), runId: String(runId) } }),
    runDecisions: (id: number, runId: number) =>
      s
        ? listDaemonRunDecisionsOptions({ path: { id: String(id), runId: String(runId) } })
        : listEmployeeRunDecisionsOptions({ path: { id: String(id), runId: String(runId) } }),
    runEnvelopes: (id: number, runId: number) =>
      s
        ? listDaemonRunExecutionEnvelopesOptions({ path: { id: String(id), runId: String(runId) } })
        : listEmployeeRunExecutionEnvelopesOptions({ path: { id: String(id), runId: String(runId) } }),
    grants: (id: number, query?: { status?: 'active' | 'revoked' | 'expired' | 'all'; limit?: string }) =>
      s
        ? listDaemonPermissionGrantsOptions({ path: path(id), query })
        : listEmployeePermissionGrantsOptions({ path: path(id), query }),
    triggers: (id: number) =>
      s ? listDaemonTriggersOptions({ path: path(id) }) : listEmployeeTriggersOptions({ path: path(id) }),
    reflections: (id: number) =>
      s ? listDaemonReflectionsOptions({ path: path(id) }) : listEmployeeReflectionsOptions({ path: path(id) }),
    findings: (
      id: number,
      query?: {
        severity?: 'info' | 'low' | 'medium' | 'high' | 'critical';
        reviewStatus?: 'pending' | 'approved' | 'rejected' | 'deferred';
      },
    ) =>
      s ? listDaemonFindingsOptions({ path: path(id), query }) : listEmployeeFindingsOptions({ path: path(id), query }),
    workflows: (id: number) =>
      s ? listDaemonWorkflowsOptions({ path: path(id) }) : listEmployeeWorkflowsOptions({ path: path(id) }),
  };
}

/** Generated query keys for invalidation, by prefix (an id-less key matches every query of that kind). */
export function daemonKeys(scope: DaemonScope) {
  const s = scope === 'system';
  return {
    list: () => (s ? listDaemonsQueryKey() : listEmployeesQueryKey()),
    summary: () => (s ? getDaemonsSummaryQueryKey() : getWorkforceSummaryQueryKey()),
    get: (id: number) => (s ? getDaemonQueryKey({ path: path(id) }) : getEmployeeQueryKey({ path: path(id) })),
    readiness: (id: number) =>
      s ? getDaemonReadinessQueryKey({ path: path(id) }) : getEmployeeReadinessQueryKey({ path: path(id) }),
    runs: (id: number) =>
      s ? listDaemonRunsQueryKey({ path: path(id) }) : listEmployeeRunsQueryKey({ path: path(id) }),
    envelopes: (id: number, runId: number) =>
      s
        ? listDaemonRunExecutionEnvelopesQueryKey({ path: { id: String(id), runId: String(runId) } })
        : listEmployeeRunExecutionEnvelopesQueryKey({ path: { id: String(id), runId: String(runId) } }),
    grants: (id: number) =>
      s
        ? listDaemonPermissionGrantsQueryKey({ path: path(id) })
        : listEmployeePermissionGrantsQueryKey({ path: path(id) }),
    triggers: (id: number) =>
      s ? listDaemonTriggersQueryKey({ path: path(id) }) : listEmployeeTriggersQueryKey({ path: path(id) }),
    findings: (id: number) =>
      s ? listDaemonFindingsQueryKey({ path: path(id) }) : listEmployeeFindingsQueryKey({ path: path(id) }),
    workflows: (id: number) =>
      s ? listDaemonWorkflowsQueryKey({ path: path(id) }) : listEmployeeWorkflowsQueryKey({ path: path(id) }),
  };
}

/** Writes for one scope: generated mutation options, ready for `useMutation`. */
export function daemonWrites(scope: DaemonScope) {
  const s = scope === 'system';
  return {
    create: () => (s ? createDaemonMutation() : createEmployeeMutation()),
    update: () => (s ? updateDaemonMutation() : updateEmployeeMutation()),
    remove: () => (s ? deleteDaemonMutation() : deleteEmployeeMutation()),
    enable: () => (s ? enableDaemonMutation() : enableEmployeeMutation()),
    disable: () => (s ? disableDaemonMutation() : disableEmployeeMutation()),
    run: () => (s ? runDaemonMutation() : runEmployeeMutation()),
    resetGovernance: () => (s ? resetDaemonGovernanceMutation() : resetEmployeeGovernanceMutation()),
    killAll: () => (s ? terminateAllDaemonsMutation() : terminateAllEmployeesMutation()),
    resumeAll: () => (s ? resumeAllDaemonsMutation() : resumeAllEmployeesMutation()),
    createTrigger: () => (s ? createDaemonTriggerMutation() : createEmployeeTriggerMutation()),
    updateTrigger: () => (s ? updateDaemonTriggerMutation() : updateEmployeeTriggerMutation()),
    deleteTrigger: () => (s ? deleteDaemonTriggerMutation() : deleteEmployeeTriggerMutation()),
    assignWorkflow: () => (s ? assignDaemonWorkflowMutation() : assignEmployeeWorkflowMutation()),
    removeWorkflow: () => (s ? removeDaemonWorkflowMutation() : removeEmployeeWorkflowMutation()),
    revokeGrant: () => (s ? revokeDaemonPermissionGrantMutation() : revokeEmployeePermissionGrantMutation()),
    grantEnvelope: () =>
      s ? grantDaemonRunExecutionEnvelopePermissionMutation() : grantEmployeeRunExecutionEnvelopePermissionMutation(),
  };
}
